import { useEffect, useState } from "react";
// Served from our own build rather than the CDN the full reader defaults to.
import pdfiumWasmUrl from "@embedpdf/pdfium/pdfium.wasm?url";

export type PdfPageImageState = {
  /** Object URL of the rendered page, or `null` until it is ready. */
  src: string | null;
  failed: boolean;
};

/**
 * Renders one page of a PDF to an image, for showing a document inline rather
 * than in the full reader. The PDF engine (a WASM build of PDFium) is loaded
 * only when there is a buffer to render.
 *
 * `width` is the CSS width the image is drawn at; the page is rendered at that
 * width times the screen's pixel ratio so it stays sharp.
 */
export function usePdfPageImage(
  buffer: ArrayBuffer | null,
  { pageIndex = 0, width = 900 }: { pageIndex?: number; width?: number } = {},
): PdfPageImageState {
  const [state, setState] = useState<PdfPageImageState>({ src: null, failed: false });

  useEffect(() => {
    if (!buffer) return;
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      const { createPdfiumEngine } = await import("@embedpdf/engines/pdfium-direct-engine");
      const engine = await createPdfiumEngine(pdfiumWasmUrl);
      try {
        // A copy: the caller keeps using the same bytes (e.g. for a download).
        const doc = await engine
          .openDocumentBuffer({ id: "page-image", content: buffer.slice(0) })
          .toPromise();
        try {
          const page = doc.pages[pageIndex] ?? doc.pages[0];
          const blob = await engine
            .renderPage(doc, page, {
              scaleFactor: width / page.size.width,
              dpr: Math.min(window.devicePixelRatio || 1, 2),
              imageType: "image/png",
            })
            .toPromise();
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setState({ src: objectUrl, failed: false });
        } finally {
          await engine.closeDocument(doc).toPromise();
        }
      } finally {
        engine.destroy?.();
      }
    })().catch(() => {
      if (!cancelled) setState({ src: null, failed: true });
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [buffer, pageIndex, width]);

  return state;
}
